import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CartCtx = createContext(null);
const KEY = "nalin_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(KEY);
      if (stored) setItems(JSON.parse(stored));
    } catch {}
  }, []);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, { size, color, quantity = 1 } = {}) => {
    const maxStock = Math.max(0, Number(product.stock ?? 0));
    if (maxStock === 0) {
      toast.error("Produto indisponível no momento");
      return false;
    }
    let added = false;
    setItems((prev) => {
      const key = `${product.id}|${size || ""}|${color || ""}`;
      const idx = prev.findIndex((i) => i.key === key);
      if (idx >= 0) {
        const target = prev[idx].quantity + quantity;
        if (target > maxStock) {
          toast.warning(`Máximo ${maxStock} unidade(s) em estoque`, { description: product.name });
          const next = [...prev];
          next[idx] = { ...next[idx], quantity: maxStock, max_stock: maxStock };
          return next;
        }
        added = true;
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: target, max_stock: maxStock };
        return next;
      }
      const qty = Math.min(quantity, maxStock);
      if (qty < quantity) toast.warning(`Somente ${maxStock} disponível(is)`);
      added = true;
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          name: product.name,
          slug: product.slug,
          image: (product.images || [])[0],
          price: product.sale_price ?? product.price,
          size, color,
          quantity: qty,
          max_stock: maxStock,
        },
      ];
    });
    if (added) toast.success("Produto adicionado ao carrinho", { description: product.name });
    return true;
  }, []);

  const updateQty = (key, qty) => {
    setItems((prev) => prev.map((i) => {
      if (i.key !== key) return i;
      const max = Math.max(1, Number(i.max_stock ?? qty));
      const safe = Math.max(1, Math.min(qty, max));
      if (qty > max) toast.warning(`Máximo ${max} unidade(s) em estoque`);
      return { ...i, quantity: safe };
    }));
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const clear = () => setItems([]);

  // Re-validate cart items against current live stock (called when opening cart / checkout).
  // For variant products, the cap is the specific color+size combo, not the product total.
  const revalidateStock = useCallback(async () => {
    if (items.length === 0) return;
    try {
      const updated = await Promise.all(
        items.map(async (i) => {
          try {
            const { data } = await api.get(`/products/${i.product_id}`);
            let max;
            if (data.has_variants) {
              const variant = (data.variants || []).find((v) => v.color === i.color && v.size === i.size);
              max = Math.max(0, Number(variant?.stock ?? 0));
            } else {
              max = Math.max(0, Number(data.stock || 0));
            }
            const nextQty = Math.min(i.quantity, max);
            return { ...i, max_stock: max, quantity: Math.max(1, nextQty), _out: max === 0 };
          } catch {
            return i;
          }
        })
      );
      setItems(updated.filter((i) => !i._out));
      const changed = updated.some((i, idx) => i.quantity !== items[idx]?.quantity || i._out);
      if (changed) toast.info("Estoque atualizado — carrinho ajustado");
    } catch {}
  }, [items]);

  const subtotal = items.reduce((acc, i) => acc + i.price * i.quantity, 0);
  const count = items.reduce((acc, i) => acc + i.quantity, 0);

  return (
    <CartCtx.Provider value={{ items, addItem, updateQty, removeItem, clear, subtotal, count, open, setOpen, revalidateStock }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
